<div class="min-h-screen bg-white p-6 print:p-0" *ngIf="!loading && workOrder">
  <!-- Print Header (only visible when printing) -->
  <div class="hidden print:block mb-4">
    <div class="flex justify-between items-center">
      <div class="text-2xl font-bold">WORK ORDER</div>
      <div class="text-sm">
        <div>Issue Date: {{ workOrder.issue_date | date:'short' }}</div>
        <div>PO Number: {{ workOrder.purchase_order_number || 'N/A' }}</div>
      </div>
    </div>
  </div>

  <!-- Action Buttons (hidden when printing) -->
  <div class="mb-6 print:hidden flex justify-between items-center">
    <button mat-button (click)="goBack()">
      <mat-icon class="mr-1">arrow_back</mat-icon>
      Back to List
    </button>
    <div class="flex gap-2">
      <button *ngIf="!isPublicView" mat-stroked-button (click)="editWorkOrder()">
        <mat-icon class="mr-1">edit</mat-icon>
        Edit
      </button>
      <button mat-raised-button color="primary" (click)="downloadPDF()">
        <mat-icon class="mr-1">picture_as_pdf</mat-icon>
        Download PDF
      </button>
      <button mat-raised-button color="accent" (click)="printWorkOrder()">
        <mat-icon class="mr-1">print</mat-icon>
        Print
      </button>
    </div>
  </div>

  <!-- Work Order Content -->
  <div class="bg-white border border-gray-200 rounded-lg p-8 print:border-0 print:rounded-none">
    <!-- Header -->
    <div class="flex justify-between items-start mb-6 print:mb-4">
      <div class="w-32 h-32 bg-gray-200 border border-gray-300 flex items-center justify-center print:w-24 print:h-24">
        <span class="text-gray-500 text-sm">LOGO</span>
      </div>
      <div class="text-right">
        <h1 class="text-4xl font-bold mb-2 print:text-3xl">WORK ORDER</h1>
        <div class="space-y-1 text-sm">
          <div><strong>Issue Date:</strong> {{ workOrder.issue_date | date:'mediumDate' }}</div>
          <div><strong>Purchase Order:</strong> {{ workOrder.purchase_order_number || 'N/A' }}</div>
        </div>
      </div>
    </div>

    <!-- Client Information -->
    <div class="mb-6 print:mb-4">
      <div class="bg-blue-600 text-white px-4 py-2 font-semibold mb-2">Client Name & Address</div>
      <div class="border border-gray-300 p-4 space-y-2">
        <div class="text-xs text-gray-600 mb-2">
          CIN-{{ workOrder.client_cin || 'XXXXXXXXXXXXX' }} | 
          PAN-{{ workOrder.client_pan || 'XXXXXXXXXXXX' }} | 
          TAN-{{ workOrder.client_tan || 'XXXXXXXXXX' }} | 
          GSTN-{{ workOrder.client_gstn || 'XXXXXXX' }}
        </div>
        <div>
          <div class="font-semibold mb-1">Bill to:</div>
          <div class="text-sm">{{ workOrder.client_name }}</div>
          <div class="text-sm">{{ workOrder.bill_to_address || workOrder.client_address }}</div>
        </div>
        <div class="mt-2">
          <div class="font-semibold mb-1">Ship to:</div>
          <div class="text-sm">{{ workOrder.ship_to_address || workOrder.bill_to_address || workOrder.client_address }}</div>
        </div>
        <div class="text-sm mt-2">
          <strong>Off Tel:</strong> {{ workOrder.client_contact_phone || 'N/A' }}
        </div>
      </div>
    </div>

    <!-- Vendor Information -->
    <div class="grid grid-cols-2 gap-4 mb-6 print:mb-4">
      <div>
        <div class="bg-blue-600 text-white px-4 py-2 font-semibold mb-2">Vendor</div>
        <div class="border border-gray-300 p-4">
          <div class="text-sm font-medium mb-2">{{ workOrder.vendor_name }}</div>
          <div class="text-sm">{{ workOrder.vendor_address }}</div>
          <div class="text-xs mt-2">
            <div><strong>GSTN:</strong> {{ workOrder.vendor_gstn || 'N/A' }}</div>
            <div><strong>PAN:</strong> {{ workOrder.vendor_pan || 'N/A' }}</div>
            <div><strong>Off Tel:</strong> {{ workOrder.vendor_off_tel || 'N/A' }}</div>
          </div>
        </div>
      </div>
      <div>
        <div class="bg-blue-600 text-white px-4 py-2 font-semibold mb-2">Vendor Contact</div>
        <div class="border border-gray-300 p-4">
          <div class="text-sm">{{ workOrder.vendor_contact_name || 'N/A' }}</div>
          <div class="text-sm">{{ workOrder.vendor_contact_phone || 'N/A' }}</div>
          <div class="text-sm">{{ workOrder.vendor_contact_email || 'N/A' }}</div>
        </div>
      </div>
    </div>

    <!-- Shipping Details -->
    <div class="grid grid-cols-2 gap-4 mb-6 print:mb-4">
      <div>
        <div class="bg-blue-600 text-white px-4 py-2 font-semibold mb-2">Client Contact</div>
        <div class="border border-gray-300 p-4">
          <div class="text-sm">{{ workOrder.client_contact_name || 'N/A' }}</div>
          <div class="text-sm">{{ workOrder.client_contact_phone || 'N/A' }}</div>
          <div class="text-sm">{{ workOrder.client_contact_email || 'N/A' }}</div>
        </div>
      </div>
      <div>
        <div class="bg-blue-600 text-white px-4 py-2 font-semibold mb-2">Shipping</div>
        <div class="border border-gray-300 p-4">
          <div class="text-sm"><strong>Ship via:</strong> {{ workOrder.ship_via || 'N/A' }}</div>
          <div class="text-sm"><strong>CIF:</strong> {{ workOrder.cif || 'N/A' }}</div>
        </div>
      </div>
    </div>

    <!-- Items Table -->
    <div class="mb-6 print:mb-4">
      <table class="w-full border-collapse border border-gray-300">
        <thead>
          <tr class="bg-blue-600 text-white">
            <th class="border border-gray-300 px-4 py-2 text-left">Item Code</th>
            <th class="border border-gray-300 px-4 py-2 text-left">Description</th>
            <th class="border border-gray-300 px-4 py-2 text-right">Quantity</th>
            <th class="border border-gray-300 px-4 py-2 text-right">Unit Price</th>
            <th class="border border-gray-300 px-4 py-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let item of workOrder.items" class="hover:bg-gray-50">
            <td class="border border-gray-300 px-4 py-2">{{ item.item_code || '-' }}</td>
            <td class="border border-gray-300 px-4 py-2">{{ item.description }}</td>
            <td class="border border-gray-300 px-4 py-2 text-right">{{ item.quantity }} {{ item.unit }}</td>
            <td class="border border-gray-300 px-4 py-2 text-right">{{ formatCurrency(item.unit_price) }}</td>
            <td class="border border-gray-300 px-4 py-2 text-right font-semibold">{{ formatCurrency(item.amount) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Payment Details and Summary -->
    <div class="grid grid-cols-3 gap-4 mb-6 print:mb-4">
      <div class="col-span-2">
        <div class="bg-gray-600 text-white px-4 py-2 font-semibold mb-2">Payment details and terms</div>
        <div class="border border-gray-300 p-4 min-h-[100px]">
          <div class="text-sm whitespace-pre-line">{{ workOrder.payment_details || workOrder.payment_terms || 'N/A' }}</div>
        </div>
      </div>
      <div>
        <div class="bg-gray-600 text-white px-4 py-2 font-semibold mb-2">Summary</div>
        <div class="border border-gray-300">
          <div class="flex justify-between px-4 py-2 bg-gray-100 border-b border-gray-300">
            <span>Sub Total</span>
            <span class="font-semibold">{{ formatCurrency(workOrder.subtotal) }}</span>
          </div>
          <div class="flex justify-between px-4 py-2 bg-gray-100 border-b border-gray-300" *ngIf="workOrder.cgst_amount > 0">
            <span>CGST &#64;{{ workOrder.cgst_percentage }}%</span>
            <span>{{ formatCurrency(workOrder.cgst_amount) }}</span>
          </div>
          <div class="flex justify-between px-4 py-2 bg-gray-100 border-b border-gray-300" *ngIf="workOrder.sgst_amount > 0">
            <span>SGST &#64;{{ workOrder.sgst_percentage }}%</span>
            <span>{{ formatCurrency(workOrder.sgst_amount) }}</span>
          </div>
          <div class="flex justify-between px-4 py-2 bg-gray-100 border-b border-gray-300" *ngIf="workOrder.igst_amount > 0">
            <span>IGST &#64;{{ workOrder.igst_percentage }}%</span>
            <span>{{ formatCurrency(workOrder.igst_amount) }}</span>
          </div>
          <div class="flex justify-between px-4 py-2 bg-gray-100 border-b border-gray-300" *ngIf="workOrder.tax2_amount > 0">
            <span>UGST &#64;{{ workOrder.tax2_percentage }}%</span>
            <span>{{ formatCurrency(workOrder.tax2_amount) }}</span>
          </div>
          <div class="flex justify-between px-4 py-2 bg-gray-100 border-b border-gray-300" *ngIf="workOrder.tax3_amount > 0">
            <span>CESS &#64;{{ workOrder.tax3_percentage }}%</span>
            <span>{{ formatCurrency(workOrder.tax3_amount) }}</span>
          </div>
          <div class="flex justify-between px-4 py-2 bg-gray-100 border-b border-gray-300" *ngIf="workOrder.tax1_amount > 0">
            <span>TDS &#64;{{ workOrder.tax1_percentage }}%</span>
            <span>{{ formatCurrency(workOrder.tax1_amount) }}</span>
          </div>
          <div class="flex justify-between px-4 py-2 bg-gray-100 border-b border-gray-300" *ngIf="workOrder.shipping_charges > 0">
            <span>Shipping charges</span>
            <span>{{ formatCurrency(workOrder.shipping_charges) }}</span>
          </div>
          <div class="flex justify-between px-4 py-2 bg-gray-100 border-b border-gray-300" *ngIf="workOrder.other_charges > 0">
            <span>Others</span>
            <span>{{ formatCurrency(workOrder.other_charges) }}</span>
          </div>
          <div class="flex justify-between px-4 py-2 bg-yellow-200 font-bold">
            <span>Total INR</span>
            <span>{{ formatCurrency(workOrder.total_amount) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Notes and Special Instructions -->
    <div class="grid grid-cols-2 gap-4 mb-6 print:mb-4">
      <div>
        <div class="text-sm mb-2">
          <strong>Note:</strong> Please contact SPOC in case of any doubts or clarifications.
        </div>
        <div class="border border-gray-300 p-4 min-h-[80px]">
          <div class="text-sm whitespace-pre-line">{{ workOrder.notes || '' }}</div>
        </div>
      </div>
      <div>
        <div class="bg-blue-600 text-white px-4 py-2 font-semibold mb-2">Special Instructions and Conditions</div>
        <div class="border border-gray-300 p-4 min-h-[80px]">
          <div class="text-sm whitespace-pre-line">{{ workOrder.special_instructions || '' }}</div>
        </div>
      </div>
    </div>

    <!-- Amount in Words -->
    <div class="border-t-2 border-gray-400 pt-4">
      <div class="text-blue-600 font-semibold mb-2">Amount in words</div>
      <div class="text-lg font-medium">{{ workOrder.amount_in_words || 'Zero Rupees Only' }}</div>
    </div>
  </div>
</div>

<!-- Loading State -->
<div *ngIf="loading" class="flex flex-col items-center justify-center py-20">
  <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
  <p class="text-gray-600">Loading work order...</p>
</div>

